import { useEffect, useState } from 'react';
import { View, type ViewStyle } from 'react-native';
import Animated, {
  type AnimatedStyle,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Popover, PopoverBackdrop, PopoverBody, PopoverContent } from '@/components/ui/popover';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { METRIC_LABEL_SIZE, METRIC_MARK_SIZE } from '@/constants/badges';
import {
  useReferenceChampionBadge,
  type ReferenceChampionBadgeData,
} from '@/hooks/useReferenceChampionBadge';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { trackReferenceBadgeOpened } from '@/util/analyticsEvents';

/**
 * States the threshold and its window, and nothing else.
 *
 * Describes a behavior, not a quality: "cited a source" is a fact about what happened, where
 * anything evaluative would be a clinical claim about a named physician. Keeps the number for
 * the same reason — a badge nobody can check against a record is not a credential — but no
 * per-expert figure, since 9 of 10 and 17 of 20 round to a number that ranks experts
 * backwards.
 *
 * Puts the expert in the verb rather than in a pronoun. An earlier phrasing had inquiries
 * "including" references, which made the inquiry the actor; leading with the verb takes the
 * expert as its implied subject and so needs no "their" beside a clinician's own photo.
 *
 * Says reference rather than source, matching the badge it explains and this project's own
 * definition, where a reference is any of four channels on a message the expert authored: an
 * assistant publication, a shared link, an attachment, or a citation typed in prose. A second
 * word for the same thing would leave a reader working out whether the badge and its
 * explanation are about one idea or two.
 *
 * Counts inquiries because that is the unit the roster query measures and the word this domain
 * uses. "At least" is load bearing and not padding: the rule admits exactly 70%, so 14 of 20
 * qualifies, and a bare "70%" would both exclude that expert and misstate everyone above the
 * floor, who range up to every inquiry they answered.
 *
 * Still short of the Open Badges model this was designed against, which pairs criteria with
 * the evidence behind them. An expert who cannot see which of their own inquiries counted has
 * to take the number on faith, and no wording fixes that — it needs the per-inquiry breakdown.
 */
const CRITERIA_EXPLANATION = 'Included a reference in at least 70% of the last 20 inquiries.';

/** Visual height of the label, used to pad the press target out to an accessible size. */
const LABEL_HEIGHT_PX = 20;

/**
 * Lifts the label onto `background-0` so it separates from a tinted host by elevation rather
 * than by a second accent color. Clears 17:1 either way it inverts (light/dark).
 */
const ELEVATED_SURFACE = 'self-start rounded-md bg-background-0 px-2 py-0.5';

/**
 * Underlines the label to mark it pressable, instead of a full border, so it doesn't read as
 * the same kind of control as the navigation chips beside it — a straight line under text
 * reads as an underline; a bordered rectangle reads as a button.
 *
 * Drawn as a bottom border rather than a text decoration, because RN's `Text` has no
 * `text-underline-offset` at all: an actual text-decoration underline sat right against the
 * descender on "Champion" with no way to nudge it clear on native, where a border's offset is
 * just padding. Dotted degrades to solid on Android for the same reason a decoration would
 * have. `typography-700` rather than the label's full color, since this is the badge's only
 * pressability signal and is held to SC 1.4.11's 3:1 (clears with room to spare).
 *
 * Positioned out of the layout flow rather than padded into it. Padding the label's own box
 * makes it taller than its text, and every neighbour then aligns to a box whose center is no
 * longer where the words are: first the mark inside the badge, then the response time sharing
 * the row outside it. Each was corrected separately before the cause was, so the rule is that
 * this decoration must never contribute height.
 */
const DEFINITION_AFFORDANCE = 'border-b border-dotted border-typography-700';

/** Clears the descenders on "Champion" without the underline occupying any height. */
const DEFINITION_UNDERLINE_OFFSET = -7;

/**
 * Drops the explanation clear of the strip the badge sits in.
 *
 * The trigger is the label, whose bottom is inside the strip's padding, so a panel anchored
 * flush to it lands on top of the strip's own edge. There is no arrow to read as a pointer,
 * having been dropped when it fell on the divider, so an overlapping panel has nothing marking
 * it as attached to anything and simply looks misplaced.
 *
 * Measured against the strip's padding rather than guessed: enough to clear it and leave a gap
 * that reads as deliberate.
 */
const POPOVER_CLEARANCE = 16;

/**
 * Holds the glint back until the surrounding page has settled, so it reads as something catching
 * the light rather than as the row still rendering. Short enough that an expert arriving from a
 * push notification is still looking at the badge when it fires.
 */
const GLINT_DELAY_MS = 260;

/** Brightening is quicker than the fade, which is what makes it read as a catch rather than a pulse. */
const GLINT_RISE_MS = 500;

const GLINT_FALL_MS = 750;

/** Quarter turn across the whole life of the glint, so it never appears to rotate back. */
const GLINT_TURN_DEGREES = 90;

const GLINT_MIN_SCALE = 0.25;

/**
 * A single glint catching the mark's top-right corner, once, on an expert's own profile.
 *
 * Fires once by choice, not by omission. Motion that repeats indefinitely alongside other
 * content falls under WCAG's requirement to offer a way to pause it, which is a control no
 * decoration should need; and a badge that never stops glinting asserts something the criteria
 * copy is deliberately careful not to assert.
 *
 * Two values rather than one: brightness has to rise and fall while the turn only ever goes
 * forward, and driving both from a single 0-1-0 sequence would spin the glint back to where it
 * started.
 *
 * Under reduced motion nothing runs at all. The glint carries no information the badge does not
 * already carry in its label, so dropping it entirely is the honest degradation rather than
 * snapping it to a resting state that would leave a stray mark on the corner.
 */
function useMarkGlint(active: boolean): AnimatedStyle<ViewStyle> {
  const intensity = useSharedValue(0);

  const turn = useSharedValue(0);

  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!active || reducedMotion) {
      return;
    }

    intensity.value = withDelay(
      GLINT_DELAY_MS,
      withSequence(
        withTiming(1, { duration: GLINT_RISE_MS, easing: Easing.out(Easing.quad) }),
        withTiming(0, { duration: GLINT_FALL_MS, easing: Easing.in(Easing.quad) })
      )
    );

    turn.value = withDelay(
      GLINT_DELAY_MS,
      withTiming(1, { duration: GLINT_RISE_MS + GLINT_FALL_MS, easing: Easing.linear })
    );
  }, [active, reducedMotion]);

  return useAnimatedStyle(() => ({
    opacity: intensity.value,
    transform: [
      { scale: GLINT_MIN_SCALE + intensity.value * (1 - GLINT_MIN_SCALE) },
      { rotate: `${turn.value * GLINT_TURN_DEGREES}deg` },
    ],
  }));
}

/**
 * Where the glint sits over the mark, in points from the mark's top-right.
 *
 * Inset far enough that the whole glint lands on the glyph, because it is white and the
 * metrics strip behind it is `background-0`: any part that overhangs the trophy has nothing to
 * register against in the light theme and simply vanishes. The emoji does not fill its own text
 * box, painting roughly the middle of a line box two thirds again its width, so clearing the top
 * takes noticeably more than the one point that visually looks like the corner. On the gold it
 * reads as a highlight on metal, which is what a glint is.
 *
 * Numbers on a plain wrapper rather than classes: NativeWind does not apply negative arbitrary
 * offsets here, and a positive offset written as a class would still have to live on the animated
 * view, whose style object Reanimated owns.
 */
const GLINT_INSET = { top: 5, right: 1 } as const;

const GLINT_SIZE_PX = 9;

const GLINT_BAR_PX = 1.5;

/**
 * The glint: two crossed bars, drawn rather than typed.
 *
 * A shape and not a character because emoji take no color, which is the same reason the mark is
 * a separate element in the first place.
 *
 * White in both themes, deliberately not a palette accent. A glint is reflected light, and light
 * has no hue of its own to inherit from a token scale; tinting it warm to match the trophy made
 * it read as a second small mark placed beside the first rather than as something happening to
 * the first. The tradeoff is that white only registers where it overlaps the glyph in the light
 * theme, which is what the inset above is for.
 *
 * Decorative in the strict sense: the badge is present and labeled without it, so it sits
 * outside SC 1.4.11 and is hidden from assistive technology. It is placed to be seen, not to
 * pass a ratio, and nothing here should be cited as a contrast claim.
 *
 * Color goes on the plain child views. A background class on an animated view does not reliably
 * paint on native, so the animated view carries transform and opacity only.
 *
 * Every dimension here is an explicit number for a reason, and this is the landmine: sizing the
 * bars as percentages of the animated view, and sizing that view with arbitrary utility classes,
 * renders correctly on web and silently paints nothing at all on native. It fails without an
 * error, on the platform least likely to be checked. Tidying these inline styles back into
 * classes would reintroduce it, so leave them as numbers.
 */
function MarkGlint({ style }: { style: AnimatedStyle<ViewStyle> }) {
  return (
    <View
      pointerEvents="none"
      aria-hidden
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={{
        position: 'absolute',
        top: GLINT_INSET.top,
        right: GLINT_INSET.right,
        width: GLINT_SIZE_PX,
        height: GLINT_SIZE_PX,
      }}>
      <Animated.View style={style}>
        <View
          className="bg-typography-white"
          style={{
            position: 'absolute',
            left: (GLINT_SIZE_PX - GLINT_BAR_PX) / 2,
            top: 0,
            width: GLINT_BAR_PX,
            height: GLINT_SIZE_PX,
            borderRadius: 1,
          }}
        />

        <View
          className="bg-typography-white"
          style={{
            position: 'absolute',
            top: (GLINT_SIZE_PX - GLINT_BAR_PX) / 2,
            left: 0,
            height: GLINT_BAR_PX,
            width: GLINT_SIZE_PX,
            borderRadius: 1,
          }}
        />
      </Animated.View>
    </View>
  );
}

type BadgeFontSize = 'text-xs' | 'text-sm';

export type ReferenceChampionExplainSource =
  | 'profile'
  | 'directory'
  | 'inquiry_sidebar'
  | 'expert_picker';

type ReferenceChampionBadgeProps = {
  expertId: string | number;
  className?: string;
  /** Matches the type scale around it: `text-xs` default (directory card), `text-sm` on profile. */
  fontSize?: BadgeFontSize;
  /**
   * Lets the label be pressed to reveal what earns it. Off by default: the directory card is
   * itself a press target, and a nested one would fight it for the tap.
   */
  explainOnPress?: boolean;
  /** Which surface the explanation was opened from, for the analytics that go with it. */
  explainSource?: ReferenceChampionExplainSource;
  /** Renders on `background-0` instead of the host's tint, for hosts without their own grouping tint. */
  elevated?: boolean;
  /**
   * Catches the mark's corner with a single glint on mount. For an expert viewing their own
   * profile only: elsewhere the badge is a fact about someone else, and animating it would make
   * a static credential twitch on every navigation into the directory.
   */
  celebrate?: boolean;
};

/**
 * Recognition badge for experts who cite on a high share of their inquiries.
 *
 * No fill or hue: every host surface is already pressable or already tinted, and the
 * desaturated palette has no accent that reads as "earned" rather than status or ranking.
 * Shares its type with the response-time stat beside it; told apart by the trailing mark and,
 * where pressable, the underline.
 *
 * Mark trails the label rather than leading it, matching the response-time flame, so a leading
 * mark doesn't leave a hole on cards without one. If the payload sets no mark, the badge and
 * the stat become indistinguishable on the directory card specifically (no outline there).
 *
 * The rate itself is never shown: a bare percent doesn't say what window it's over.
 *
 * Opens its explanation in a popover, not inline, since growing the row in place used to
 * reflow the whole directory grid on every open/close.
 *
 * Renders nothing unless the flag is on and the expert is on its roster (checked here, not by
 * the host, so this drops in unconditionally — tests rendering a host must mock the hook).
 */
export function ReferenceChampionBadge({
  expertId,
  className,
  fontSize,
  explainOnPress,
  explainSource,
  elevated,
  celebrate,
}: ReferenceChampionBadgeProps) {
  const badge = useReferenceChampionBadge(expertId);

  if (!badge) {
    return null;
  }

  return (
    <ReferenceChampionBadgeContent
      badge={badge}
      expertId={expertId}
      className={className}
      fontSize={fontSize}
      explainOnPress={explainOnPress}
      explainSource={explainSource}
      elevated={elevated}
      celebrate={celebrate}
    />
  );
}

export function ReferenceChampionBadgeContent({
  badge,
  className,
  fontSize = 'text-xs',
  explainOnPress = false,
  explainSource,
  elevated = false,
  celebrate = false,
  expertId,
}: {
  badge: ReferenceChampionBadgeData;
  className?: string;
  fontSize?: BadgeFontSize;
  explainOnPress?: boolean;
  explainSource?: ReferenceChampionExplainSource;
  elevated?: boolean;
  celebrate?: boolean;
  expertId?: string | number;
}) {
  const [isExplaining, setIsExplaining] = useState(false);

  const glint = useMarkGlint(celebrate);

  /*
   * The affordance wraps the label in its own box rather than decorating the label's Text
   * directly, since a bottom border needs a box to draw on.
   *
   * That box is taller than the text it holds, and the row centers boxes rather than glyphs, so
   * the mark alone would settle below the label's optical center by half the underline's height.
   * Giving the mark the same trailing space, drawn as a transparent border of the same width,
   * puts both glyphs the same distance above their own box center, which is what makes them
   * read as level. Matching classes rather than a hand-tuned offset, so the two cannot drift if
   * the affordance's padding changes.
   */
  const renderLabel = (isDefinition: boolean) => (
    <HStack className="min-w-0 items-center gap-2">
      <HStack className="relative min-w-0">
        <Text
          className={twJoin('font-normal text-typography-950', METRIC_LABEL_SIZE[fontSize])}
          numberOfLines={1}>
          {badge.label}
        </Text>

        {isDefinition ? (
          <HStack
            pointerEvents="none"
            className={twJoin('absolute left-0 right-0', DEFINITION_AFFORDANCE)}
            style={{ bottom: DEFINITION_UNDERLINE_OFFSET }}
          />
        ) : null}
      </HStack>

      {/* One step above the label size — matched exactly, an emoji's padding reads as shrunken. */}
      {/*
       * Nothing in the badge moves. The glint is an overlay on a mark that is already correct
       * underneath, so a stalled animation costs the flourish and leaves the badge intact —
       * unlike animating the mark itself, where a spring that settles short strands it visibly
       * shrunken. The label is left alone for a second reason: it shares a row with the response
       * time, and one of two adjacent facts moving while the other holds still reads as a layout
       * bug rather than as a flourish.
       *
       * `shrink-0` sits on the wrapper because the wrapper is now the flex item the row measures.
       * The wrapper also anchors the absolutely-positioned glint, and being outside the label's
       * truncating box it cannot disturb the width chain that keeps a long label on one line.
       */}
      {badge.emoji ? (
        <View className="relative shrink-0">
          <Text className={METRIC_MARK_SIZE[fontSize]} aria-hidden>
            {badge.emoji}
          </Text>

          {celebrate ? <MarkGlint style={glint} /> : null}
        </View>
      ) : null}
    </HStack>
  );

  if (!explainOnPress) {
    return (
      <HStack
        data-component="reference-champion-badge"
        className={twJoin(
          'min-w-0 items-center gap-2',
          elevated && ELEVATED_SURFACE,
          className ?? ''
        )}
        {...getAccessibilityProps({ label: badge.label })}>
        {renderLabel(false)}
      </HStack>
    );
  }

  return (
    <Popover
      placement="bottom start"
      offset={POPOVER_CLEARANCE}
      onOpen={() => {
        setIsExplaining(true);

        if (expertId !== undefined && explainSource) {
          trackReferenceBadgeOpened({ expertId, source: explainSource });
        }
      }}
      onClose={() => setIsExplaining(false)}
      trigger={(triggerProps: Record<string, unknown>) => (
        // Padding only when elevated — unlifted, the label is bare text and an inset misaligns it.
        // `aria-expanded` is set alongside `accessibilityState`: RNW drops `expanded` from the
        // latter, so web announced no open/closed state without it (verified in the rendered DOM).
        //
        // No fill for the open state. It used to step to `bg-background-100` on top of the
        // resting `elevated` chip, but a host without `elevated` has no chip to step from, so
        // the fill instead popped a filled rounded box into existence on press — exactly the
        // button look this badge has no fill anywhere else to avoid. The popover appearing is
        // already unambiguous proof the press worked.
        <Pressable
          {...triggerProps}
          data-component="reference-champion-badge"
          hitSlop={getAccessibleHitSlop(LABEL_HEIGHT_PX)}
          className={twJoin(
            'min-w-0 self-start rounded-md',
            elevated && 'bg-background-0 px-2 py-0.5',
            className ?? ''
          )}
          {...getAccessibilityProps({
            label: badge.label,
            role: 'button',
            hint: isExplaining ? 'Hides what earns this badge' : 'Shows what earns this badge',
          })}
          accessibilityState={{ expanded: isExplaining }}
          aria-expanded={isExplaining}>
          {renderLabel(true)}
        </Pressable>
      )}>
      <PopoverBackdrop />

      {/* No PopoverArrow: it lands on the metrics band's divider and reads as a stray diamond. */}
      {/*
       * Tighter than any `size` variant offers. Those are scaled for panels with a header,
       * body and footer, so their smallest still wraps a single sentence in more padding than
       * the sentence is tall. Elevation belongs in the shared base because every popover needs
       * it; padding does not, because it follows the content.
       *
       * Narrower again than the next stop up, which ran to within a hair of the screen edge on a
       * phone and broke the sentence with a single word orphaned on the last line. The panel is
       * anchored to the label rather than to the column, so a width that looks reasonable in the
       * abstract sits off-centre and crowds one edge. Sized off the spacing scale rather than a
       * measured pixel, so it tracks the same rhythm as everything around it.
       */}
      <PopoverContent size="sm" className="max-w-64 p-3">
        <PopoverBody>
          {/* Matches the badge label's own ink, since this is the text that label promises. */}
          <Text
            data-component="reference-champion-criteria"
            className="text-xs text-typography-950">
            {CRITERIA_EXPLANATION}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
